import { CAMERA_OPTIONS } from 'enums/streaming';
import { CameraSource, WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import {
  ICameraStreamRequestMsg,
  ICameraStreamResponseMsg,
} from 'lib_ts/interfaces/i-machine-msg';
import { createContext, FC, ReactNode, useEffect, useState } from 'react';
import { StreamingService } from 'services/streaming.service';
import { WebSocketService } from 'services/web-socket.service';

const CONTEXT_NAME = 'StreamingContext';
const DEFAULT_REFRESH_RATE = 5;
export interface IStreamingContext {
  source: CameraSource;
  readonly setSource: (value: CameraSource) => void;

  paused: boolean;
  readonly togglePaused: () => void;

  lastFrame: Partial<ICameraStreamResponseMsg> | undefined;

  refreshRate: number;
  readonly setRefreshRate: (value: number) => void;
}

const DEFAULT: IStreamingContext = {
  source: CameraSource.BI_R,
  setSource: () => console.error(`${CONTEXT_NAME}: not init`),

  paused: false,
  togglePaused: () => console.error(`${CONTEXT_NAME}: not init`),

  lastFrame: undefined,

  refreshRate: DEFAULT_REFRESH_RATE,
  setRefreshRate: () => console.error(`${CONTEXT_NAME}: not init`),
};

export const StreamingContext = createContext(DEFAULT);

interface IProps {
  machineID: string;
  defaultSource?: CameraSource;
  children: ReactNode;
}

export const StreamingProvider: FC<IProps> = (props) => {
  const [source, setSource] = useState(
    props.defaultSource ?? (CAMERA_OPTIONS[0].value as CameraSource)
  );

  const [paused, setPaused] = useState(DEFAULT.paused);
  const [refreshRate, setRefreshRate] = useState(DEFAULT.refreshRate);
  const [lastFrame, setLastFrame] = useState(DEFAULT.lastFrame);

  // handle requesting the latest frame based on the selected source
  useEffect(() => {
    if (paused) {
      return;
    }

    const frameDelayMs = Math.round(1000 / refreshRate);
    // queue up the next frame update request
    const timeout = setTimeout(async () => {
      try {
        const frame = await StreamingService.getInstance().getLastFrame({
          machineID: props.machineID,
          source: source,
        });

        setLastFrame(frame);
      } catch (e) {
        console.error(e);
      }
    }, frameDelayMs);

    return () => clearTimeout(timeout);
  }, [paused, lastFrame, source]);

  // tells the server that a streamer is tuning into whichever source
  useEffect(() => {
    console.debug(`enabling camera source ${source}`);

    const msg: ICameraStreamRequestMsg = {
      machineID: props.machineID,
      source: source,
      enable: true,
    };

    WebSocketService.send(WsMsgType.U2M_CameraStream, msg, CONTEXT_NAME);

    setLastFrame(undefined);

    return () => {
      console.debug(`disabling camera source ${source}`);

      const msg: ICameraStreamRequestMsg = {
        machineID: props.machineID,
        source: source,
        enable: false,
      };

      WebSocketService.send(WsMsgType.U2M_CameraStream, msg, CONTEXT_NAME);
      setLastFrame(undefined);
    };
  }, [source]);

  const state: IStreamingContext = {
    source: source,
    setSource: setSource,

    paused: paused,
    togglePaused: () => setPaused((prev) => !prev),

    lastFrame: lastFrame,

    refreshRate,
    setRefreshRate: setRefreshRate,
  };

  return (
    <StreamingContext.Provider value={state}>
      {props.children}
    </StreamingContext.Provider>
  );
};
